import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import KantzLogo from "../../assets/logo.svg";
import { FullPageLoader } from "../../modules/authentication/layout";
import { describeScope } from "./scopeDescriptions";
import { useConsentDetails } from "./useConsentDetails";

const PageContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  padding: theme.spacing(3),
}));

const Card = styled("div")(({ theme }) => ({
  width: "100%",
  maxWidth: 740,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 8,
  padding: theme.spacing(6, 7),
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
}));

const LogoWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginBottom: theme.spacing(3),
}));

const Title = styled("h1")(({ theme }) => ({
  textAlign: "center",
  fontSize: 24,
  fontWeight: 600,
  margin: `0 0 ${theme.spacing(3)}`,
  color: theme.palette.text.primary,
}));

const Subtitle = styled("p")(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(4),
}));

const ClientName = styled("span")(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
}));

const PrincipalName = styled("span")(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const SectionTitle = styled("h2")(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.text.primary,
  margin: `0 0 ${theme.spacing(2)}`,
}));

const ScopeList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(4),
}));

const ScopeItem = styled("label")(() => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 4,
  cursor: "pointer",
}));

const ScopeContent = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 0,
  flex: 1,
  lineHeight: 1.5,
});

const ScopeName = styled("span")(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

const ScopeDescription = styled("span")(() => ({
  fontSize: 14,
  color: "#6D6B6B",
}));

const ScopeBadge = styled("span")(({ theme }) => ({
  display: "inline-block",
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(0.25, 1),
  fontSize: 12,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.grey[100],
  borderRadius: 4,
  width: "fit-content",
}));

const Actions = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const Footer = styled("p")(() => ({
  textAlign: "center",
  fontSize: 14,
  color: "#6D6B6B",
  margin: 0,
  lineHeight: 1.6,
}));

export const ConsentPage: React.FC = () => {
  const { t } = useTranslation("consent");
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("client_id");
  const scope = searchParams.get("scope");
  const state = searchParams.get("state");

  const consentQuery = useConsentDetails({ clientId, scope, state });

  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (consentQuery.data) {
      // Pre-select previously approved scopes
      setSelectedScopes(new Set(consentQuery.data.previouslyApproved));
    }
  }, [consentQuery.data]);

  const toggleScope = (scopeValue: string) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scopeValue)) {
        next.delete(scopeValue);
      } else {
        next.add(scopeValue);
      }
      return next;
    });
  };

  const handleAllow = () => {
    if (!consentQuery.data) return;
    submitConsent(
      consentQuery.data.authorizeEndpoint,
      consentQuery.data.clientId,
      consentQuery.data.state,
      selectedScopes
    );
  };

  const handleCancel = () => {
    if (!consentQuery.data) return;
    // Empty scope set — SAS interprets as access_denied
    submitConsent(
      consentQuery.data.authorizeEndpoint,
      consentQuery.data.clientId,
      consentQuery.data.state,
      new Set()
    );
  };

  if (consentQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (consentQuery.isError || !consentQuery.data) {
    return (
      <PageContainer>
        <Card>
          <Title>{t("errors.title")}</Title>
          <Subtitle>
            {consentQuery.error?.message ?? t("errors.load-failed")}
          </Subtitle>
        </Card>
      </PageContainer>
    );
  }

  const consent = consentQuery.data;

  return (
    <PageContainer>
      <Card>
        <LogoWrapper>
          <img src={KantzLogo} alt="Kantz" width={64} height={64} />
        </LogoWrapper>

        <Title>{t("title")}</Title>

        <Subtitle>
          <Trans
            i18nKey="subtitle"
            t={t}
            values={{
              clientName: consent.clientName,
              principalName: consent.principalName,
            }}
            components={{
              clientName: <ClientName />,
              principalName: <PrincipalName />,
            }}
          />
        </Subtitle>

        <SectionTitle>{t("review-permissions")}</SectionTitle>

        <ScopeList>
          {consent.requestedScopes.map((scopeValue) => {
            const { name, description } = describeScope(scopeValue, t);
            return (
              <ScopeItem key={scopeValue}>
                <Checkbox
                  checked={selectedScopes.has(scopeValue)}
                  onChange={() => toggleScope(scopeValue)}
                  color="primary"
                  size="medium"
                />
                <ScopeContent>
                  <ScopeName>{name}</ScopeName>
                  <ScopeDescription>{description}</ScopeDescription>
                  {consent.previouslyApproved.includes(scopeValue) && (
                    <ScopeBadge>{t("previously-approved")}</ScopeBadge>
                  )}
                </ScopeContent>
              </ScopeItem>
            );
          })}
        </ScopeList>

        <Actions>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{ width: "140px", color: "#6D6B6B", fontWeight: 700 }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAllow}
            disabled={selectedScopes.size === 0}
            sx={{ width: "140px" }}
          >
            {t("buttons.allow")}
          </Button>
        </Actions>

        <Footer>
          <Trans i18nKey="footer" t={t} components={{ strong: <strong /> }} />
        </Footer>
      </Card>
    </PageContainer>
  );
};

/**
 * Submits the consent decision as a POST form to /oauth2/authorize.
 * Uses a real HTML form (not fetch) because SAS expects a browser-style
 * form submission that triggers a redirect on success.
 */
function submitConsent(
  authorizeEndpoint: string,
  clientId: string,
  state: string,
  scopes: Set<string>
): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = authorizeEndpoint;

  const addField = (name: string, value: string) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };

  addField("client_id", clientId);
  addField("state", state);
  scopes.forEach((s) => addField("scope", s));

  const csrfToken = getCsrfTokenFromCookie();
  if (csrfToken) {
    addField("_csrf", csrfToken);
  }

  document.body.appendChild(form);
  form.submit();
}

/**
 * Reads the CSRF token from the XSRF-TOKEN cookie set by Spring Security.
 */
function getCsrfTokenFromCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}
